import type { CurvaDocument } from "@/document/model/document";
import { findSceneNode, getSceneNodeChildren } from "@/document/model/document";
import type { PrimitiveNode, SceneNode } from "@/document/model/nodes";
import type { NodeEffect, NodeStyle, Paint } from "@/document/model/styles";
import {
  normalizeGradientPaint,
  normalizeImagePaint,
  normalizeStrokeStack,
  styleWithStrokeStack,
  type GradientPaint,
  type ImagePaint,
  type LinearGradientPaint,
  type RadialGradientPaint,
} from "@/document/model/styles";
import type { RichTextModel, TextHorizontalAlign, TextVerticalAlign, TypeStyle } from "@/document/model/text";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { imageRectForPaint, rectToNormalizedCrop } from "@/document/operations/imagePaintGeometry";
import { normalizeDegrees, rectCenter, transformRotationOrigin } from "@/lib/math/rotation";
import type { Rect } from "@/lib/math/rect";
import { contoursToPathData, pathDataToContours, transformContours } from "./pathGeometry";
import { getVectorNetworkBounds } from "./vectorGeometry";
import { nodeEffectiveWorldRotation, nodeLocalPlacementFromWorldGeometry, nodeSelectionCorners } from "./nodeWorldTransform";

export type FlipAxis = "horizontal" | "vertical";

export function geometryMirrorsOnFlip(node: SceneNode): boolean {
  return node.type === "vectorNetwork"
    || node.type === "shape"
    || node.type === "boolean"
    || (node.type === "primitive" && node.primitive.kind === "line");
}

export function flipSceneNode(node: SceneNode, referenceBounds: Rect, axis: FlipAxis): void {
  const rotationOrigin = transformRotationOrigin(node.bounds, node.transform);
  mirrorNodeBounds(node, referenceBounds, axis);
  flipNodeTransform(node, referenceBounds, axis, rotationOrigin);

  if (node.type === "group" || node.type === "frame" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    for (const child of node.children) {
      flipSceneNode(child, node.bounds, axis);
    }
    return;
  }

  flipNodeContent(node, axis);
}

export function flipSceneNodeInWorld(document: CurvaDocument, nodeId: string, referenceWorldBounds: Rect, axis: FlipAxis): void {
  const node = findSceneNode(document, nodeId);
  const parentId = findParentId(document, nodeId);
  const worldCorners = nodeSelectionCorners(document, nodeId);
  if (!node || !parentId || !worldCorners) {
    return;
  }

  const referenceCenter = rectCenter(referenceWorldBounds);
  const mirroredCorners = worldCorners.map(corner => axis === "horizontal"
    ? { x: referenceCenter.x * 2 - corner.x, y: corner.y }
    : { x: corner.x, y: referenceCenter.y * 2 - corner.y });
  const worldRotation = nodeEffectiveWorldRotation(document, nodeId);
  const mirroredWorldRotation = normalizeDegrees(-worldRotation);
  const placement = nodeLocalPlacementFromWorldGeometry(document, parentId, mirroredCorners, mirroredWorldRotation);

  node.bounds = placement.bounds;
  node.transform = placement.rotation === 0
    ? { ...node.transform, rotation: 0, x: 0, y: 0 }
    : {
        ...node.transform,
        rotation: placement.rotation,
        x: placement.rotationOrigin?.x ?? rectCenter(placement.bounds).x,
        y: placement.rotationOrigin?.y ?? rectCenter(placement.bounds).y,
      };
  flipNodeContentInPlace(node, axis);
}

function flipNodeContentInPlace(node: SceneNode, axis: FlipAxis): void {
  if (node.type === "group" || node.type === "frame" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    for (const child of node.children) {
      flipSceneNode(child, node.bounds, axis);
    }
    return;
  }

  flipNodeContent(node, axis);
}

function flipNodeContent(node: SceneNode, axis: FlipAxis): void {
  if (node.type === "text") {
    node.text = flipTextModel(node.text, axis);
    if (axis === "horizontal") {
      node.transform = { ...node.transform, scaleX: node.transform.scaleX * -1 };
    } else {
      node.transform = { ...node.transform, scaleY: node.transform.scaleY * -1 };
    }
    return;
  }

  if (node.type === "vectorNetwork") {
    flipVectorNetwork(node.network, node.bounds, axis);
    node.bounds = getVectorNetworkBounds(node.network);
  } else if (node.type === "shape") {
    const mirroredBounds = mirroredContentBounds(node.bounds, axis);
    node.path = contoursToPathData(transformContours(pathDataToContours(node.path), node.bounds, mirroredBounds));
  } else if (node.type === "boolean") {
    const mirroredBounds = mirroredContentBounds(node.bounds, axis);
    const contours = transformContours(node.resolved.contours, node.bounds, mirroredBounds);
    node.resolved = {
      contours,
      path: contoursToPathData(contours),
    };
    for (const source of node.sourceNodes) {
      flipSceneNode(source, node.bounds, axis);
    }
  } else if (node.type === "primitive") {
    flipPrimitiveNode(node, axis);
  }

  if (usesTransformScaleForImageFlip(node)) {
    applyAxisFlipScale(node, axis);
  }

  node.style = flipNodeStyle(node.style, node.bounds, axis);
}

function usesTransformScaleForImageFlip(node: SceneNode): boolean {
  if (node.type === "text") {
    return false;
  }

  if (node.type === "primitive" && node.primitive.kind === "line") {
    return false;
  }

  return true;
}

function applyAxisFlipScale(node: SceneNode, axis: FlipAxis): void {
  if (axis === "horizontal") {
    node.transform = { ...node.transform, scaleX: node.transform.scaleX * -1 };
  } else {
    node.transform = { ...node.transform, scaleY: node.transform.scaleY * -1 };
  }
}

function mirrorNodeBounds(node: SceneNode, referenceBounds: Rect, axis: FlipAxis): void {
  const referenceCenter = rectCenter(referenceBounds);
  const center = rectCenter(node.bounds);
  const mirroredCenter = axis === "horizontal"
    ? { x: referenceCenter.x * 2 - center.x, y: center.y }
    : { x: center.x, y: referenceCenter.y * 2 - center.y };

  node.bounds = {
    x: mirroredCenter.x - node.bounds.width / 2,
    y: mirroredCenter.y - node.bounds.height / 2,
    width: node.bounds.width,
    height: node.bounds.height,
  };
}

function flipNodeTransform(
  node: SceneNode,
  referenceBounds: Rect,
  axis: FlipAxis,
  rotationOrigin: { x: number; y: number },
): void {
  const rotation = Number.isFinite(node.transform.rotation) ? node.transform.rotation : 0;
  let nextRotation = axis === "horizontal"
    ? normalizeDegrees(-rotation)
    : normalizeDegrees(180 - rotation);

  // Unrotated objects flip through mirrored geometry or transform scale; avoid
  // baking an extra 180° rotation that incorrectly flips image fills twice.
  if (rotation === 0) {
    nextRotation = 0;
  }
  const hasCustomOrigin = rotation !== 0 || node.transform.x !== 0 || node.transform.y !== 0;

  if (!hasCustomOrigin) {
    node.transform = {
      ...node.transform,
      rotation: nextRotation,
    };
    return;
  }

  const referenceCenter = rectCenter(referenceBounds);
  const mirroredOrigin = axis === "horizontal"
    ? { x: referenceCenter.x * 2 - rotationOrigin.x, y: rotationOrigin.y }
    : { x: rotationOrigin.x, y: referenceCenter.y * 2 - rotationOrigin.y };

  node.transform = {
    ...node.transform,
    rotation: nextRotation,
    x: mirroredOrigin.x,
    y: mirroredOrigin.y,
  };
}

function flipPrimitiveNode(node: PrimitiveNode, axis: FlipAxis): void {
  if (node.primitive.kind === "rectangle") {
    node.primitive = {
      ...node.primitive,
      cornerRadii: flipCornerRadii(node.primitive.cornerRadii, axis),
    };
    return;
  }

  if (node.primitive.kind === "line") {
    const mirroredBounds = mirroredContentBounds(node.bounds, axis);
    node.bounds = {
      x: mirroredBounds.x,
      y: mirroredBounds.y,
      width: mirroredBounds.width,
      height: mirroredBounds.height,
    };
  }
}

function flipVectorNetwork(network: VectorNetwork, bounds: Rect, axis: FlipAxis): void {
  const mirroredBounds = mirroredContentBounds(bounds, axis);
  const mirrorPoint = (point: { x: number; y: number }) => ({
    x: mirroredBounds.x + (point.x - bounds.x) * (mirroredBounds.width / (bounds.width || 1)),
    y: mirroredBounds.y + (point.y - bounds.y) * (mirroredBounds.height / (bounds.height || 1)),
  });

  for (const vertex of network.vertices) {
    const next = mirrorPoint(vertex);
    vertex.x = next.x;
    vertex.y = next.y;
  }

  for (const edge of network.edges) {
    if (edge.from.handle) {
      edge.from.handle = mirrorPoint(edge.from.handle);
    }
    if (edge.to.handle) {
      edge.to.handle = mirrorPoint(edge.to.handle);
    }
  }
}

function mirroredContentBounds(bounds: Rect, axis: FlipAxis): Rect {
  if (axis === "horizontal") {
    return {
      x: bounds.x + bounds.width,
      y: bounds.y,
      width: -bounds.width,
      height: bounds.height,
    };
  }

  return {
    x: bounds.x,
    y: bounds.y + bounds.height,
    width: bounds.width,
    height: -bounds.height,
  };
}

function flipCornerRadii(
  radii: [number, number, number, number],
  axis: FlipAxis,
): [number, number, number, number] {
  const [topLeft, topRight, bottomRight, bottomLeft] = radii;
  return axis === "horizontal"
    ? [topRight, topLeft, bottomLeft, bottomRight]
    : [bottomLeft, bottomRight, topRight, topLeft];
}

export function flipNodeStyle(style: NodeStyle, bounds: Rect, axis: FlipAxis): NodeStyle {
  const fills = style.fills.map(paint => flipPaint(paint, bounds, axis));
  const strokes = normalizeStrokeStack(style).map(stroke => ({
    ...stroke,
    paint: flipPaint(stroke.paint, bounds, axis),
  }));

  return styleWithStrokeStack(
    {
      ...style,
      fills,
      effects: style.effects?.map(effect => flipEffect(effect, axis)),
    },
    strokes,
  );
}

function flipPaint(paint: Paint, bounds: Rect, axis: FlipAxis): Paint {
  if (paint.type === "solid") {
    return paint;
  }

  if (paint.type === "image") {
    return bakeImagePaintCrop(paint, bounds);
  }

  return flipGradientPaint(paint, axis);
}

function flipGradientPaint(paint: GradientPaint, axis: FlipAxis): GradientPaint {
  const normalized = normalizeGradientPaint(paint);
  if (normalized.type === "linearGradient") {
    return flipLinearGradientPaint(normalized, axis);
  }

  return flipRadialGradientPaint(normalized, axis);
}

function flipLinearGradientPaint(paint: LinearGradientPaint, axis: FlipAxis): LinearGradientPaint {
  if (paint.start && paint.end) {
    return normalizeGradientPaint({
      ...paint,
      start: flipGradientPoint(paint.start, axis),
      end: flipGradientPoint(paint.end, axis),
    });
  }

  const angle = axis === "horizontal"
    ? normalizeGradientAngle(180 - paint.angle)
    : normalizeGradientAngle(-paint.angle);

  return normalizeGradientPaint({
    ...paint,
    angle,
    start: undefined,
    end: undefined,
  });
}

function flipRadialGradientPaint(paint: RadialGradientPaint, axis: FlipAxis): RadialGradientPaint {
  const center = paint.center ?? { x: 0.5, y: 0.5 };
  const radiusPoint = paint.radiusPoint ?? { x: center.x, y: center.y + paint.radius };

  return normalizeGradientPaint({
    ...paint,
    center: flipGradientPoint(center, axis),
    radiusPoint: flipGradientPoint(radiusPoint, axis),
  });
}

function flipGradientPoint(point: { x: number; y: number }, axis: FlipAxis): { x: number; y: number } {
  return axis === "horizontal"
    ? { x: 1 - point.x, y: point.y }
    : { x: point.x, y: 1 - point.y };
}

function bakeImagePaintCrop(paint: ImagePaint, bounds: Rect): ImagePaint {
  const normalized = normalizeImagePaint(paint);
  if (normalized.fillMode === "crop") {
    return normalized;
  }

  return normalizeImagePaint({
    ...normalized,
    fillMode: "crop",
    crop: rectToNormalizedCrop(bounds, imageRectForPaint(bounds, normalized)),
  });
}

function flipEffect(effect: NodeEffect, axis: FlipAxis): NodeEffect {
  if (effect.type === "dropShadow" || effect.type === "innerShadow") {
    return axis === "horizontal"
      ? { ...effect, offsetX: -effect.offsetX }
      : { ...effect, offsetY: -effect.offsetY };
  }

  return effect;
}

function flipTextModel(text: RichTextModel, axis: FlipAxis): RichTextModel {
  return {
    ...text,
    style: flipTypeStyle(text.style, axis),
    styleOverrideTable: Object.fromEntries(
      Object.entries(text.styleOverrideTable).map(([id, override]) => [id, flipTypeStylePartial(override, axis)]),
    ),
  };
}

function flipTypeStyle(style: TypeStyle, axis: FlipAxis): TypeStyle {
  return {
    ...style,
    horizontalAlign: axis === "horizontal" ? flipHorizontalAlign(style.horizontalAlign) : style.horizontalAlign,
    verticalAlign: axis === "vertical" ? flipVerticalAlign(style.verticalAlign) : style.verticalAlign,
  };
}

function flipTypeStylePartial(style: Partial<TypeStyle>, axis: FlipAxis): Partial<TypeStyle> {
  return {
    ...style,
    ...(style.horizontalAlign && axis === "horizontal" ? { horizontalAlign: flipHorizontalAlign(style.horizontalAlign) } : {}),
    ...(style.verticalAlign && axis === "vertical" ? { verticalAlign: flipVerticalAlign(style.verticalAlign) } : {}),
  };
}

function findParentId(document: CurvaDocument, nodeId: string): string | undefined {
  for (const page of document.pages) {
    const parentId = findParentIdInChildren(page.children, nodeId, page.id);
    if (parentId) {
      return parentId;
    }
  }
  return undefined;
}

function findParentIdInChildren(children: SceneNode[], nodeId: string, parentId: string): string | undefined {
  for (const child of children) {
    if (child.id === nodeId) {
      return parentId;
    }

    const nestedChildren = getSceneNodeChildren(child);
    if (!nestedChildren) {
      continue;
    }

    const nested = findParentIdInChildren(nestedChildren, nodeId, child.id);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

function flipHorizontalAlign(align: TextHorizontalAlign): TextHorizontalAlign {
  if (align === "LEFT") {
    return "RIGHT";
  }
  if (align === "RIGHT") {
    return "LEFT";
  }
  return align;
}

function flipVerticalAlign(align: TextVerticalAlign): TextVerticalAlign {
  if (align === "TOP") {
    return "BOTTOM";
  }
  if (align === "BOTTOM") {
    return "TOP";
  }
  return align;
}

function normalizeGradientAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}
